<template>
  <div class="container-fluid">
    <div class="row" id="app">
        <!-- Inputs Section -->
        <div class="col-12">
            <h4>Stress State</h4>
        </div>

        <!-- σx Input -->
        <div class="col-6">
          <label for="sigma_x">σx (MPa): <span>{{ sx }}</span></label>
          <input 
            type="range" 
            id="sigma_x" 
            v-model.number="sx" 
            min="-100" 
            max="100" 
            step="1"
            class="form-range"
          />
          <p class="fst-italic small">Tension is positive</p>
        </div>

        <!-- σy Input -->
        <div class="col-6">
          <label for="sigma_y">σy (MPa): <span>{{ sy }}</span></label>
          <input 
            type="range" 
            id="sigma_y" 
            v-model.number="sy" 
            min="-100" 
            max="100" 
            step="1"
            class="form-range"
          />
          <p class="fst-italic small">Tension is positive</p>
        </div>

        <!-- τxy Input -->
        <div class="col-6">
          <label for="tau_xy">τxy (MPa): <span>{{ txy }}</span></label>
          <input 
            type="range" 
            id="tau_xy" 
            v-model.number="txy" 
            min="-100" 
            max="100" 
            step="1"
            class="form-range"
          />
          <p class="fst-italic small">CCW is positive</p>
        </div>

        <!-- θ Input -->
        <div class="col-6">
          <label for="theta">θ (Deg.): <span>{{ theta }}</span></label>
          <input 
            type="range" 
            id="theta" 
            v-model.number="theta" 
            min="-90" 
            max="90" 
            step="1"
            class="form-range"
          />
          <p class="fst-italic small">Rotation from current state (CCW is positive)</p>
        </div>
      
      
      <!-- Report Section -->
      <div class="col-12">
        <div class="visualizer-container" ref="container">
          <Visualizer :descriptor="chartDescriptor" :width="svgSize" :height="svgSize" ref="visualizer"/>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import * as d3 from 'd3';
  import Visualizer from './Visualizer.vue';     

  export default {
    name: "MohrCircle",
    components: {
      Visualizer
    },
    data: function () {
      return {
        svgSize: 500,
        sx: 100,
        sy: 100,
        txy: 100,
        theta: 30,
        colors: ["#568e49", "#ccec84", "#94ad64", "#248894", "#bc3c4c", "#d4b4c4", "#273c75"],
        pointR: 5
      };
    },
    mounted() {
      this.updateSize();
      window.addEventListener('resize', this.updateSize);
      this.renderAxes();
    },
    updated() {
      this.renderAxes();
    },
    beforeUnmount() {
      window.removeEventListener('resize', this.updateSize);
    },
    methods: {
        updateSize() {
          if (this.$refs.container) {
            const containerWidth = this.$refs.container.offsetWidth;
            this.svgSize = Math.min(containerWidth, 500);
          }
        },
        convDegToRad: function(deg) {
            return deg * Math.PI / 180;
        },
        getYAxisX: function() {
            return this.s1 < 0 ? this.xScale(this.s1) : this.s2 > 0 ? this.xScale(this.s2) : this.xScale(0);
        },
        renderAxes: function() {
            this.$nextTick(() => {
            if (this.$refs.visualizer && this.$refs.visualizer.$refs.svg) {
                const svg = d3.select(this.$refs.visualizer.$refs.svg);
                
                // Remove existing axes
                svg.selectAll('.x-axis').remove();
                svg.selectAll('.y-axis').remove();
                
                // Add X axis with fewer ticks
                svg.append('g')
                .attr('class', 'x-axis')
                .attr('transform', `translate(0,${this.MC.cy})`)
                .style('font-size', this.axisFontSize + 'px')
                .call(d3.axisTop(this.xScale).ticks(5));
                
                // Add Y axis with fewer ticks
                svg.append('g')
                .attr('class', 'y-axis')
                .attr('transform', `translate(${this.getYAxisX()},0)`)
                .style('font-size', this.axisFontSize + 'px')
                .call(d3.axisLeft(this.yScale).ticks(5));
            }
            });
        },
        generateMohrCircle: function () {
            return [
                {
                    id: "mohrCircle",
                    type: "circle",
                    cx: this.MC.cx,
                    cy: this.MC.cy,
                    r: this.MC.r,
                    fill: this.colors[1],
                    stroke: this.colors[0],
                    strokeWidth: 3,
                    fillOpacity: 0.75
                },
                {
                    id: "centerOfMohrCircle",
                    type: "circle",
                    cx: this.MC.cx,
                    cy: this.MC.cy,
                    r: this.pointR,
                    fill: "black"
                },
                {
                    id: "pointA",
                    type: "circle",
                    cx: this.xScale(this.sx),
                    cy: this.yScale(-1 * this.txy),
                    r: this.pointR,
                    fill: "black"
                },
                {
                    id: "pointAText",
                    type: "text",
                    x: this.xScale(this.sx),
                    y: this.yScale(-1 * this.txy) + 20,
                    text: `${this.sx}, -${this.txy}`,
                    fontSize: this.textFontSize + 'px'
                },
                {
                    id: "pointB",
                    type: "circle",
                    cx: this.xScale(this.sy),
                    cy: this.yScale(this.txy),
                    r: this.pointR,
                    fill: "black"
                },
                {
                    id: "pointBText",
                    type: "text",
                    x: this.xScale(this.sy),
                    y: this.yScale(this.txy) - 20,
                    text: `${this.sy}, ${this.txy}`,
                    fontSize: this.textFontSize + 'px'
                },
                {
                    id: "lineAB",
                    type: "line",
                    points: [
                        { x: this.xScale(this.sx), y: this.yScale(-1 * this.txy) },
                        { x: this.xScale(this.sy), y: this.yScale(this.txy) }
                    ],
                    stroke: this.colors[0],
                    strokeWidth: 3
                },
                {
                    id: "lineAX",
                    type: "line",
                    points: [
                        { x: this.xScale(this.sx), y: this.yScale(-1 * this.txy) },
                        { x: this.xScale(this.sx), y: this.yScale(0) }
                    ],
                    fill: "black",
                    lineType: "dashed"
                },
                {
                    id: "lineAY",
                    type: "line",
                    points: [
                        { x: this.xScale(this.sx), y: this.yScale(-1 * this.txy) },
                        { x: this.getYAxisX(), y: this.yScale(-1 * this.txy) }
                    ],
                    fill: "black",
                    lineType: "dashed"
                },
                {
                    id: "lineBX",
                    type: "line",
                    points: [
                        { x: this.xScale(this.sy), y: this.yScale(this.txy) },
                        { x: this.xScale(this.sy), y: this.yScale(0) }
                    ],
                    fill: "black",
                    lineType: "dashed"
                },
                {
                    id: "lineBY",
                    type: "line",
                    points: [
                        { x: this.xScale(this.sy), y: this.yScale(this.txy) },
                        { x: this.getYAxisX(), y: this.yScale(this.txy) }
                    ],
                    fill: "black",
                    lineType: "dashed"
                },
                {
                    id: "pointC",
                    type: "circle",
                    cx: this.xScale(this.tss.sxp),
                    cy: this.yScale(-1 * this.tss.sxyp),
                    r: this.pointR,
                    fill: this.colors[4]
                },
                {
                    id: "pointCText",
                    type: "text",
                    x: this.xScale(this.tss.sxp),
                    y: this.yScale(-1 * this.tss.sxyp),
                    fontSize: this.textFontSize + 'px'
                },
                {
                    id: "pointD",
                    type: "circle",
                    cx: this.xScale(this.tss.syp),
                    cy: this.yScale(this.tss.sxyp),
                    r: this.pointR,
                    fill: this.colors[4]
                },
                {
                    id: "pointDText",
                    type: "text",
                    x: this.xScale(this.tss.syp),
                    y: this.yScale(this.tss.sxyp),
                    fontSize: this.textFontSize + 'px'
                },
                {
                    id: "lineCD",
                    type: "line",
                    points: [
                        { x: this.xScale(this.tss.sxp), y: this.yScale(-1 * this.tss.sxyp) },
                        { x: this.xScale(this.tss.syp), y: this.yScale(this.tss.sxyp) }
                    ],
                    stroke: this.colors[4],
                    strokeWidth: 3
                },
                {
                    id: "lineCX",
                    type: "line",
                    points: [
                        { x: this.xScale(this.tss.sxp), y: this.yScale(-1 * this.tss.sxyp) },
                        { x: this.xScale(this.tss.sxp), y: this.yScale(0) }
                    ],
                    stroke: this.colors[4],
                    lineType: "dashed"
                },
                {
                    id: "lineCY",
                    type: "line",
                    points: [
                        { x: this.xScale(this.tss.sxp), y: this.yScale(-1 * this.tss.sxyp) },
                        { x: this.getYAxisX(), y: this.yScale(-1 * this.tss.sxyp) }
                    ],
                    stroke: this.colors[4],
                    lineType: "dashed"
                },
                {
                    id: "lineDX",
                    type: "line",
                    points: [
                        { x: this.xScale(this.tss.syp), y: this.yScale(this.tss.sxyp) },
                        { x: this.xScale(this.tss.syp), y: this.yScale(0) }
                    ],
                    stroke: this.colors[4],
                    lineType: "dashed"
                },
                {
                    id: "lineDY",
                    type: "line",
                    points: [
                        { x: this.xScale(this.tss.syp), y: this.yScale(this.tss.sxyp) },
                        { x: this.getYAxisX(), y: this.yScale(this.tss.sxyp) }
                    ],
                    stroke: this.colors[4],
                    lineType: "dashed"
                },
                {
                    id: "pointP1",
                    type: "circle",
                    cx: this.xScale(this.s1),
                    cy: this.yScale(0),
                    r: this.pointR,
                    fill: this.colors[5]
                },
                {
                    id: "pointP2",
                    type: "circle",
                    cx: this.xScale(this.s2),
                    cy: this.yScale(0),
                    r: this.pointR,
                    fill: this.colors[5]
                }
            ];
        },
        generateStateOfStress: function (
            x,
            y,
            size,
            rotate = 0,
            stressVals = {},
            style = {},
            id
            ) {
            const arrowsLength = size * 0.6;
            const tab = size * 0.1;
            const fontSize = size / 75 + "rem";
            const el = {
                x: arrowsLength,
                y: arrowsLength,
                width: size,
                height: size
            };

            return {
                type: "group",
                id,
                x,
                y,
                rotate: { t: rotate, x: el.x + 0.5 * el.width, y: el.y + 0.5 * el.height },
                children: [
                {
                    type: "rect",
                    ...el,
                    fill: style.fill || "none",
                    strokeWidth: 2
                },
                {
                    type: "line",
                    points: [
                    { x: el.x - 2 * tab, y: el.y + 0.5 * el.height },
                    { x: el.x - 2 * tab - arrowsLength, y: el.y + 0.5 * el.height }
                    ],
                    markerEnd: "url(#arrow)"
                },
                {
                    type: "text",
                    text: stressVals.sxx || "\u03c3" + "xx",
                    x: el.x - 2 * tab - arrowsLength,
                    y: el.y + 0.5 * el.height - tab,
                    rotate: 0,
                    fontSize
                },
                {
                    type: "line",
                    points: [
                    { x: el.x + el.width + 2 * tab, y: el.y + 0.5 * el.height },
                    {
                        x: el.x + el.width + 2 * tab + arrowsLength,
                        y: el.y + 0.5 * el.height
                    }
                    ],
                    markerEnd: "url(#arrow)"
                },
                {
                    type: "text",
                    text: stressVals.sxx || "\u03c3" + "xx",
                    x: el.x + el.width + 2 * tab + arrowsLength,
                    y: el.y + 0.5 * el.height - tab,
                    rotate: 0,
                    fontSize
                },
                {
                    type: "line",
                    points: [
                    { x: el.x + 0.5 * el.width, y: el.y - 2 * tab },
                    { x: el.x + 0.5 * el.width, y: el.y - 2 * tab - arrowsLength }
                    ],
                    markerEnd: "url(#arrow)"
                },
                {
                    type: "text",
                    text: stressVals.syy || "\u03c3" + "yy",
                    x: el.x + 0.5 * el.width,
                    y: el.y - 2 * tab - arrowsLength - tab,
                    rotate: 0,
                    fontSize
                },
                {
                    type: "line",
                    points: [
                    { x: el.x + 0.5 * el.width, y: el.y + el.height + 2 * tab },
                    {
                        x: el.x + 0.5 * el.width,
                        y: el.y + el.height + 2 * tab + arrowsLength
                    }
                    ],
                    markerEnd: "url(#arrow)"
                },
                {
                    type: "text",
                    text: stressVals.syy || "\u03c3" + "yy",
                    x: el.x + 0.5 * el.width,
                    y: el.y + el.height + 2 * tab + arrowsLength + 2 * tab,
                    rotate: 0,
                    fontSize
                },
                {
                    type: "line",
                    id: "not working",
                    center: { x: el.x + 0.5 * el.width, y: el.y - tab },
                    length: arrowsLength,
                    angle: 0,
                    markerEnd: "url(#arrow)"
                },
                {
                    type: "text",
                    text: stressVals.sxy || "\u03c3" + "yx",
                    x: el.x + el.width + 2 * tab,
                    y: el.y - tab,
                    rotate: 0,
                    fontSize
                },
                {
                    type: "line",
                    center: { x: el.x + 0.5 * el.width, y: el.y + el.height + tab },
                    length: arrowsLength,
                    angle: 180,
                    markerEnd: "url(#arrow)"
                },
                {
                    type: "text",
                    text: stressVals.sxy || "\u03c3" + "yx",
                    x: el.x - 2 * tab,
                    y: el.y + el.height + 2 * tab,
                    rotate: 0,
                    fontSize
                },
                {
                    type: "line",
                    center: { x: el.x - tab, y: el.y + 0.5 * el.height },
                    length: arrowsLength,
                    angle: 90,
                    markerEnd: "url(#arrow)"
                },
                {
                    type: "text",
                    text: stressVals.sxy || "\u03c3" + "xy",
                    x: el.x - 4 * tab,
                    y: el.y + el.height,
                    rotate: 0,
                    fontSize
                },
                {
                    type: "line",
                    center: { x: el.x + el.width + tab, y: el.y + 0.5 * el.height },
                    length: arrowsLength,
                    angle: 270,
                    markerEnd: "url(#arrow)"
                },
                {
                    type: "text",
                    text: stressVals.sxy || "\u03c3" + "xy",
                    x: el.x + el.width + 4 * tab,
                    y: el.y + tab,
                    rotate: 0,
                    fontSize
                }
                ]
            };
            }
    },
    computed: {
        // Average normal stress
        savg: function() {
            return 0.5 * (this.sx + this.sy);
        },
        // Radius of Mohr's circle
        R: function() {
            return Math.sqrt((this.sx - this.savg) ** 2 + this.txy ** 2);
        },
        // Principal stress 1
        s1: function() {
            return this.savg + this.R;
        },
        // Principal stress 2
        s2: function() {
            return this.savg - this.R;
        },
        // Principal angle
        thetaP: function() {
            return Math.atan(this.txy / (this.sx - this.sy));
        },
        // Transformed Stress State
        tss: function() {
            const t = this.convDegToRad(this.theta);
            return {
                sxp:
                    Math.cos(t) ** 2 * this.sx +
                    Math.sin(t) ** 2 * this.sy +
                    2 * Math.sin(t) * Math.cos(t) * this.txy,
                syp:
                    Math.sin(t) ** 2 * this.sx +
                    Math.cos(t) ** 2 * this.sy -
                    2 * Math.sin(t) * Math.cos(t) * this.txy,
                sxyp:
                    -1 * Math.sin(t) * Math.cos(t) * this.sx +
                    Math.sin(t) * Math.cos(t) * this.sy +
                    (Math.cos(t) ** 2 - Math.sin(t) ** 2) * this.txy
            };
        },
        xScale: function() {
            return d3
                .scaleLinear()
                .domain([this.s2, this.s1])
                .range([0.2 * this.svgSize, 0.8 * this.svgSize]);
        },
        yScale: function() {
            return d3
                .scaleLinear()
                .domain([this.R, -1 * this.R])
                .range([this.MC.cy - 0.3 * this.svgSize, this.MC.cy + 0.3 * this.svgSize]);
        },
        // Location of the mohr circle on screen
        MC: function() {
            return {
                cx: 0.5 * this.svgSize,
                cy: 0.55 * this.svgSize,
                r: 0.3 * Math.min(this.svgSize, this.svgSize)
            };
        },
        // Chart descriptor
        chartDescriptor: function() {
            const mohrCircle = this.generateMohrCircle(this.s1, this.s2, this.R);
            return [...mohrCircle];
        },
        // Responsive font sizes
        textFontSize: function() {
            return Math.max(8, this.svgSize * 0.020); // Min 8px, smaller scale
        },
        axisFontSize: function() {
            return Math.max(7, this.svgSize * 0.018); // Min 7px, smaller scale
        }
    }
  };
</script>

<style scoped>
.visualizer-container {
  width: 100%;
  max-width: 500px;
  margin: 0 auto;
}

input[type="range"] {
  width: 100%;
  height: 6px !important;
  padding: 0 !important;
  margin: 8px 0 !important;
  border: none !important;
  background: #dcdcdc;
}

.small {
  font-size: 0.75rem;
  margin-top: 2px;
}
</style>